import { morphSection } from '@theme/section-renderer';

/**
 * Cart line editing safety net — undo-after-remove, and committing a typed quantity.
 *
 * Two findings drove this:
 *
 * 1. Removing a cart line was instant and irreversible. Baymard lists "provide an undo
 *    option after item removal" among its five cart-quantity requirements, and Nielsen is
 *    explicit that confirmation dialogs are the wrong tool for routine actions — they get
 *    habituated away and stop preventing anything. Undo is the pattern that survives.
 *    The undo affordance here deliberately does NOT auto-dismiss: a control that vanishes
 *    before it can be read is the documented failure mode of toasts.
 *
 * 2. The quantity input only committed on `blur`. Baymard observed sites where quantity
 *    edits went undetected and orders were placed at the wrong quantity for exactly this
 *    reason. Pressing Enter now commits instead of doing nothing (or submitting the form).
 *
 * This is a global module rather than an inline script because the cart re-renders by
 * morphing: parsed <script> tags never execute after a morph, which is what killed the
 * drawer add-on buttons in June. Everything below is delegated from `document`.
 *
 * Deliberately NOT offered for lines the server owns: the free gift, bundle children, and
 * selling-plan (subscription) lines. Re-adding those from the client would race the reward
 * logic or drop the plan, and a wrong cart is far worse than a missing undo.
 */

/** @type {{key: string, id: number, quantity: number, properties: object|null, title: string} | null} */
let pendingUndo = null;
let cartSnapshot = null;
let undoInFlight = false;

function sectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
    .filter(Boolean);
}

async function fetchCart() {
  try {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    return null;
  }
}

async function refreshSnapshot() {
  cartSnapshot = await fetchCart();
}

function morphSections(sections) {
  if (!sections) return;
  for (const [id, html] of Object.entries(sections)) {
    if (!html) continue;
    try {
      morphSection(id, html);
    } catch (error) {
      console.error(`cart-line-controls: unable to morph ${id}`, error);
    }
  }
}

/**
 * Lines whose composition the server or an app controls. Offering undo on these would let
 * the client re-add something the reward logic is about to recalculate.
 */
function isServerOwnedLine(item) {
  if (!item) return true;
  if (item.properties && item.properties._free_gift === 'true') return true;
  if (item.selling_plan_allocation) return true;
  // Bundle children carry a parent relationship; removing the parent takes them with it.
  if (item.parent_key || item.bundle_id) return true;
  return false;
}

function findLineByKey(key) {
  if (!cartSnapshot || !key) return null;
  return (cartSnapshot.items || []).find((item) => item.key === key) || null;
}

/**
 * The /cart.js snapshot can lag the page: the rewards module adds the free gift after load,
 * and apps re-render lines, either of which can leave a row whose key is not in the cached
 * cart. The row itself carries everything undo needs, so fall back to it rather than
 * silently dropping the undo. Anything the fallback cannot see (selling plans, line
 * properties) is treated as server-owned by the caller.
 */
function lineFromRow(row) {
  const key = row.dataset.key || null;
  const snapshotLine = findLineByKey(key);
  if (snapshotLine) return snapshotLine;

  const variantId = Number(row.dataset.variantId || 0);
  if (!variantId) return null;
  if (row.dataset.parentKey) return null;

  return {
    key,
    variant_id: variantId,
    quantity: Number(row.dataset.quantity || 1),
    properties: {},
    product_title: row.dataset.productTitle || 'that item',
    fromRow: true,
  };
}

function announce(message) {
  let region = document.getElementById('cart-live-region');
  if (!region) {
    region = document.createElement('div');
    region.id = 'cart-live-region';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('role', 'status');
    region.className = 'visually-hidden';
    document.body.appendChild(region);
  }
  // Reassigning identical text does not re-announce; clear first.
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
}

function undoHosts() {
  // Drawer first, then the cart page. Both can be in the DOM at once. cart-items-component
  // is the backstop for states that render neither wrapper, such as an emptied cart.
  const hosts = Array.from(document.querySelectorAll('.cart-drawer__items, .cart-items__wrapper'));
  if (hosts.length) return hosts;
  return Array.from(document.querySelectorAll('cart-items-component'));
}

function clearUndoBars() {
  document.querySelectorAll('[data-cart-undo-bar]').forEach((node) => node.remove());
}

function renderUndoBar() {
  clearUndoBars();
  if (!pendingUndo) return;

  for (const host of undoHosts()) {
    const bar = document.createElement('div');
    bar.setAttribute('data-cart-undo-bar', '');
    bar.className = 'cart-undo';
    bar.setAttribute('role', 'status');

    const text = document.createElement('span');
    text.className = 'cart-undo__text';
    text.textContent = `Removed ${pendingUndo.title}`;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cart-undo__button';
    button.setAttribute('data-cart-undo-button', '');
    button.textContent = 'Undo';

    bar.append(text, button);
    host.prepend(bar);
  }
}

async function performUndo() {
  if (!pendingUndo || undoInFlight) return;
  undoInFlight = true;

  const restore = pendingUndo;
  const buttons = document.querySelectorAll('[data-cart-undo-button]');
  buttons.forEach((button) => {
    button.disabled = true;
    button.textContent = 'Restoring…';
  });

  try {
    const body = {
      id: restore.id,
      quantity: restore.quantity,
      sections: sectionIds().join(',') || undefined,
      sections_url: window.location.pathname,
    };
    if (restore.properties && Object.keys(restore.properties).length) {
      body.properties = restore.properties;
    }

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`Undo failed: ${response.status}`);

    const data = await response.json();
    pendingUndo = null;
    clearUndoBars();
    morphSections(data.sections);
    await refreshSnapshot();
    announce(`${restore.title} restored to your cart`);
    // Let the rest of the theme (rewards bar, recommendations, cart bubble) recalculate.
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data, source: 'cart-undo' } }));
  } catch (error) {
    console.error('cart-line-controls: undo failed', error);
    buttons.forEach((button) => {
      button.disabled = false;
      button.textContent = 'Undo';
    });
    announce('Could not restore that item. Please add it again.');
  } finally {
    undoInFlight = false;
  }
}

/**
 * Capture phase so the snapshot is taken while the line still exists — the theme's own
 * handler removes the row on the same click.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-cart-undo-button]')) {
      event.preventDefault();
      performUndo();
      return;
    }

    const removeButton = target.closest('.cart-items__remove');
    if (!removeButton) return;

    const row = removeButton.closest('[data-cart-item]');
    if (!(row instanceof HTMLElement)) return;
    const key = row.dataset.key || null;
    const line = lineFromRow(row);

    if (!line || isServerOwnedLine(line)) {
      pendingUndo = null;
      clearUndoBars();
      return;
    }

    pendingUndo = {
      key,
      id: line.variant_id || line.id,
      quantity: line.quantity,
      properties: line.properties || null,
      title: line.product_title || line.title || 'that item',
    };
    announce(`${pendingUndo.title} removed from your cart`);
  },
  true
);

/**
 * Enter used to do nothing (or submit the cart form). Blur commits the value through the
 * theme's existing `on:blur="/setQuantity"` binding, so this stays a one-line behaviour
 * change rather than a second code path that can disagree with the first.
 */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  if (input.name !== 'updates[]') return;
  event.preventDefault();
  input.blur();
});

document.addEventListener('cart:update', () => {
  refreshSnapshot().then(() => {
    if (!pendingUndo) return;
    // Only offer undo once the line is actually gone; a failed remove leaves it in place.
    if (pendingUndo.key && findLineByKey(pendingUndo.key)) {
      pendingUndo = null;
      clearUndoBars();
      return;
    }
    renderUndoBar();
  });
});

/**
 * The bar is injected into a region the cart re-renders by morphing, and one removal can
 * trigger a second morph — dropping below the gift threshold makes the rewards module pull
 * the free gift, which re-renders the cart again and wipes the bar with it. Rather than
 * hunting for a container outside every morph target, re-attach whenever the bar goes
 * missing while an undo is still on offer.
 */
const undoGuard = new MutationObserver(() => {
  if (!pendingUndo || undoInFlight) return;
  if (document.querySelector('[data-cart-undo-bar]')) return;
  if (!undoHosts().length) return;
  renderUndoBar();
});
undoGuard.observe(document.body, { childList: true, subtree: true });

// Closing the drawer ends the undo window — the bar should never outlive the context it
// was shown in and reappear later attached to a different cart.
document.addEventListener('dialog:close', () => {
  pendingUndo = null;
  clearUndoBars();
});

refreshSnapshot();
